import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";

export interface SignatureCanvasHandle {
  getSignature: () => string | null;
  clear: () => void;
}

interface SignatureCanvasProps {
  className?: string;
}

const SignatureCanvas = forwardRef<SignatureCanvasHandle, SignatureCanvasProps>(({ className = "" }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useImperativeHandle(ref, () => ({
    getSignature: () => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      return canvas.toDataURL("image/png");
    },
    clear: handleClear,
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      ctx.scale(ratio, ratio);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#01545D";
      ctx.putImageData(imageData, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getContextAndPoint = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { ctx: null, x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    return { ctx, x, y };
  };

  const handlePointerDown = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    const { ctx, x, y } = getContextAndPoint(evt);
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    (evt.target as HTMLCanvasElement).setPointerCapture(evt.pointerId);
  };

  const handlePointerMove = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const { ctx, x, y } = getContextAndPoint(evt);
    if (!ctx) return;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    try {
      (evt.target as HTMLCanvasElement).releasePointerCapture(evt.pointerId);
    } catch {
      // ignore
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div className={"flex flex-col gap-2 " + className}>
      <div className="relative w-full h-16 md:h-16 border-2 border-dashed border-[#DCE1E8] rounded-xl bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDrawing}
          onPointerLeave={stopDrawing}
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-[#818BA0] text-base">Sign here</span>
        </div>
      </div>
      <button
        type="button"
        onClick={handleClear}
        className="self-start text-sm text-[#01545D] font-semibold underline px-2 py-1"
      >
        Clear
      </button>
    </div>
  );
});

SignatureCanvas.displayName = "SignatureCanvas";

export default SignatureCanvas;
